import React from 'react';
import {
    Flex,
    Button,
    Text,
    Select,
    useColorModeValue,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

const Pagination = ({
    data,
    onPageChange,
    initialItemsPerPage = 10,
    currentPage,
    totalItems,
    totalPages,
    hasMoreData
}) => {
    const [itemsPerPage, setItemsPerPage] = React.useState(10);

    const textColor = useColorModeValue('gray.600', 'white');
    const activeButtonBg = useColorModeValue('navy.700', 'navy.700');
    const activeButtonColor = useColorModeValue('white', 'white');

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

    const handlePageChange = (page) => {
        if (page >= 1) {
            onPageChange(data, page, itemsPerPage);
        }
    };

    const handleItemsPerPageChange = (value) => {
        const newItemsPerPage = Number(value);
        setItemsPerPage(newItemsPerPage);
        onPageChange(data, 1, newItemsPerPage);
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            let endPage = startPage + maxVisiblePages - 1;

            if (endPage > totalPages) {
                endPage = totalPages;
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }

            if (startPage > 1) {
                pages.push(1);
                if (startPage > 2) {
                    pages.push('...');
                }
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                    pages.push('...');
                }
                pages.push(totalPages);
            }
        }

        return pages;
    };

    return (
        <Flex justify="space-between" align="center" w="100%" mt={4}>
            <Text color={textColor} fontSize="sm">
                Showing {totalItems > 0 ? startIndex + 1 : 0} to {endIndex} of {totalItems} entries
            </Text>

            <Flex align="center" gap={2}>
                <Flex align="center" mr={4}>
                    <Text color={textColor} fontSize="sm" mr={2}>
                        Show
                    </Text>
                    <Select
                        size="sm"
                        width="70px"
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(e.target.value)}
                        borderRadius="4px"
                        defaultValue={10}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </Select>
                </Flex>

                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                    isDisabled={currentPage === 1}
                    p={0}
                    minW="32px"
                    h="32px"
                    borderRadius="16px"
                    _hover={{ bg: "#5835A8", color: "white" }}
                >
                    <ChevronLeftIcon />
                </Button>

                {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                        <Text key={`ellipsis-${index}`} _hover={{ color: "white" }} color={textColor} mx={1}>...</Text>
                    ) : (
                        <Button
                            key={page}
                            size="sm"
                            variant={currentPage === page ? 'solid' : 'outline'}
                            bg={currentPage === page ? activeButtonBg : 'transparent'}
                            color={currentPage === page ? activeButtonColor : textColor}
                            onClick={() => handlePageChange(page)}
                            minW="32px"
                            h="32px"
                            p={0}
                            borderRadius="16px"
                            _hover={{ bg: "#5835A8", color: "white" }}
                        >
                            {page}
                        </Button>
                    )
                ))}

                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                    isDisabled={!hasMoreData}
                    p={0}
                    minW="32px"
                    h="32px"
                    borderRadius="16px"
                    _hover={{ bg: "#5835A8", color: "white" }}
                >
                    <ChevronRightIcon />
                </Button>
            </Flex>
        </Flex>
    );
};

export default Pagination;